import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAuth } from "@/lib/route-guards";
import { LoadingProvider } from "@/contexts/loading-context";
import { handleApiError } from "@/lib/error-handler";
import { useState, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Marquee from "react-fast-marquee";
import { Button } from "@/components/ui/button";
import { Menu, Edit, Home, List, Play, Trophy } from "lucide-react";
import SlotCounter from "react-slot-counter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useKuraStartSignalR } from "@/hooks/use-kura-start-signalr";
import { toast } from "sonner";
import { useKuraStartDetailQuery } from "@/hooks/use-kura-queries";
import { useSettingsQuery } from "@/hooks/use-settings-queries";
import { Skeleton } from "@/components/ui/skeleton";
import { api, notifyStopDrawOnClose } from "@/lib/api";


export const Route = createFileRoute("/kura/$kuraId/start")({
  beforeLoad: requireAuth,
  component: () => (
    <LoadingProvider>
      <KuraStartPage />
    </LoadingProvider>
  ),
});

function KuraStartPage() {
  const { kuraId } = Route.useParams();

  // Fetch kura start details from API
  const {
    data: kuraStartDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useKuraStartDetailQuery(kuraId);

  // Counter states - these will update during the kura draw
  // Counter shows the current winner's Çekiliş Sıra No (starts at 0)
  const [counter1, setCounter1] = useState(0); // Current winner's draw sequence number
  const [counter2, setCounter2] = useState(0); // Property counter (if needed)

  // SignalR connection state
  const [kuraStarted, setKuraStarted] = useState(false);

  // Ref to track if stop was requested (to prevent race condition with async start API)
  const stopRequestedRef = useRef(false);

  // Ref to track if start toast has been shown (to avoid showing it multiple times)
  const startToastShownRef = useRef(false);

  // Winners list from SignalR
  // const [winners, setWinners] = useState<ParticipantDto[]>([]);
  const [winners, setWinners] = useState<Record<string, any>[]>([]);

  // Fetch settings using React Query
  const { data: settings } = useSettingsQuery();

  // Get lottery kind from API response
  // 1 = Sadece Katılımcı, 2 = Katılımcı + Konut, 3 = Sadece Konut
  const lotteryKind = kuraStartDetail?.lotteryKind || 1;
  // Kind 4 ("Katılımcı + Konut + Diğer + 65 Yaş") canlı çekilişte kind 2 ile aynı davranır;
  // farkı yalnızca konutun hangi katılımcıya gittiğidir.
  const isParticipantPropertyKind = lotteryKind === 2 || lotteryKind === 4;

  // Initialize SignalR connection for kura start
  const signalR = useKuraStartSignalR({
    kuraId,
    onNewWinner: (participant) => {
      // Only set kuraStarted to true if stop was not requested
      // This prevents race condition where SignalR events arrive after user clicked stop
      if (!stopRequestedRef.current) {
        setKuraStarted(true);
      }

      // Add the new winner at the beginning of the list (latest first)
      setWinners((prev) => [participant, ...prev]);

      // Debug: Log the participant data structure
      console.log("Participant data received:", participant);

      // Update counter1 to show current winner's Participant Sıra No
      // Find the participant column with isOrderNo flag (totalParticipants is not null)
      const participantOrderNoColumn = kuraStartDetail?.getLotterySettingsList.find(
        col => col.isOrderNo && col.totalParticipants !== null
      );
      if (participantOrderNoColumn) {
        const columnName = participantOrderNoColumn.columnName;
        if (columnName && participant[columnName] !== undefined) {
          const siraNo = participant[columnName];
          const siraNoNumber = typeof siraNo === "string"
            ? parseInt(siraNo, 10)
            : siraNo;
          setCounter1(siraNoNumber);
        }
      }

      // Update counter2 to show current winner's Property Sıra No
      // Find the property column with isOrderNo flag (totalProperties is not null)
      const propertyOrderNoColumn = kuraStartDetail?.getLotterySettingsList.find(
        col => col.isOrderNo && col.totalProperties !== null
      );
      if (propertyOrderNoColumn) {
        const columnName = propertyOrderNoColumn.columnName;
        if (columnName && participant[columnName] !== undefined) {
          const propertySiraNo = participant[columnName];
          const propertySiraNoNumber = typeof propertySiraNo === "string"
            ? parseInt(propertySiraNo, 10)
            : propertySiraNo;
          setCounter2(propertySiraNoNumber);
        }
        else {
          setCounter2(0);
        }
      }
    },
    onKuraCompleted: () => {
      setKuraStarted(false);
      // Reset stop flag when kura completes
      stopRequestedRef.current = false;
      // Show completion message
      toast.success(
        <span className="text-xl font-extrabold whitespace-nowrap">
          KURA ÇEKİLİŞİ TAMAMLANDI
        </span>,
        {
          description: (
            <span className="text-xl font-semibold whitespace-nowrap">
              KURA TAMAMLANMIŞTIR.
            </span>
          ),
          duration: 5000,

        }
      );
    },
    onCounterUpdate: (counter1, counter2) => {
      if (counter1 !== undefined) setCounter1(counter1);
      if (counter2 !== undefined) setCounter2(counter2);
    },
  });

  useEffect(() => {
    const list = kuraStartDetail?.resultList ?? [];

    if (list.length > 0) {
      setWinners((prev) => {
        // If API has more results than current state (e.g., SignalR delivered
        // a few winners before the API query resolved), use API data since
        // it contains the full historical result set.
        if (prev.length >= list.length) return prev;
        return list;
      });
    }
  }, [kuraStartDetail]);

  // InOrder to notify backend when user leaves the page while kura is being drawn
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!kuraStarted) return;

      notifyStopDrawOnClose(kuraId);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [kuraId, kuraStarted]);



  // Keyboard shortcuts: Ctrl+B to start, Ctrl+M to stop kura
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+B: Start kura
      if (event.ctrlKey && event.key === "b") {
        event.preventDefault();
        if (!kuraStarted) {
          handleStartKura();
        }
        return;
      }

      // Ctrl+M: Stop kura
      if (event.ctrlKey && event.key === "m") {
        event.preventDefault();
        if (kuraStarted) {
          handleStopKura();
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kuraStarted, lotteryKind]);

  // Function to start the kura draw - calls backend API
  const handleStartKura = async () => {
    try {
      // Reset flags when starting
      stopRequestedRef.current = false;
      startToastShownRef.current = false;

      // Set kuraStarted immediately to update UI (button changes to stop)
      setKuraStarted(true);

      startToastShownRef.current = true;

      // Determine the endpoint based on lotteryKind
      // 1 = Sadece Katılımcı, 2 = Katılımcı + Konut, 3 = Sadece Konut
      // Kind 4 kendi kuyruğunu ve tüketicisini kullanır.
      const endpoint =
        lotteryKind === 4
          ? `/lotteries/${kuraId}/start-realtype`
          : isParticipantPropertyKind
            ? `/lotteries/${kuraId}/start-participant-property`
            : `/lotteries/${kuraId}/start`;

      // Call backend REST API to start the draw
      const response = await api.post(endpoint);

      if (response.status !== 200) {
        throw new Error("Kura başlatılamadı");
      }
    } catch (error: any) {
      console.error("Error starting kura:", error);

      // Extract user-friendly error message
      const errorMessage = handleApiError(error, "Kura başlatılamadı");
      toast.error(errorMessage);
      setKuraStarted(false);
    }
  };

  // Function to stop the kura draw
  const handleStopKura = async () => {
    try {
      // Set flag to prevent start toast from showing if start API is still in flight
      stopRequestedRef.current = true;

      if (!signalR.isConnected) {
        toast.error("Bağlantı kurulamadı.");
        return;
      }

      // Call backend REST API to stop the draw
      const response = await api.post(`/lotteries/${kuraId}/stop-draw`);

      if (response.status !== 200) {
        throw new Error("Kura durdurulamadı");
      }

      setKuraStarted(false);
      toast.success(
        <span className="text-2xl font-extrabold whitespace-nowrap">
          KURA ÇEKİLİŞİ DURDURULDU
        </span>,
        {
          duration: 5000,
          className: "p-12 flex items-center",
        }
      );
    } catch (error) {
      console.error("Error stopping kura:", error);
      const errorMessage = handleApiError(error, "Kura durdurulamadı");
      toast.error(errorMessage);
    }
  };

  // Get kura name from API data
  const kuraName = kuraStartDetail?.lotteryName || "";

  // Marquee text from settings API
  const marqueeText = settings?.description || "";

  // Get property count from API response
  const propertyCount = kuraStartDetail?.propertiesTotalCount || 0;

  // Calculate digit counts for counters based on actual totals
  const participantDigits = Math.max(
    2,
    (kuraStartDetail?.participantTotalCount || 0).toString().length
  );
  const propertyDigits = Math.max(
    2,
    (kuraStartDetail?.propertiesTotalCount || 0).toString().length
  );

  // Stats from API data
  const kuraStats = [
    {
      label: "Toplam Hak Sahibi",
      value: isDetailLoading
        ? "..."
        : kuraStartDetail?.participantTotalCount?.toString() || "0",
      show: true, // Always show
    },
    {
      label: "Toplam Konut Sayısı",
      value: isDetailLoading ? "..." : propertyCount.toString(),
      show: propertyCount > 0, // Only show if > 0
    },
    {
      label: "Asil Sayısı",
      value: isDetailLoading
        ? "..."
        : kuraStartDetail?.primaryCount?.toString() || "0",
      show: true, // Always show
    },
    {
      label: "Yedek Sayısı",
      value: isDetailLoading
        ? "..."
        : kuraStartDetail?.backupCount?.toString() || "0",
      show: true, // Always show
    },
  ].filter((stat) => stat.show); // Filter out stats that shouldn't be shown

  // Image URL from API - use lotteryImage if available, otherwise use settings byteDosya
  // Add base64 prefix for byteDosya images
  const getImageUrl = () => {
    if (kuraStartDetail?.lotteryImage) {
      // Eğer zaten data:image ile başlamıyorsa prefix ekle
      if (!kuraStartDetail.lotteryImage.startsWith("data:image")) {
        return `data:image/png;base64,${kuraStartDetail.lotteryImage}`;
      }
      return kuraStartDetail.lotteryImage;
    }

    if (settings?.byteDosya) {
      return `data:image/png;base64,${settings.byteDosya}`;
    }

    return "";
  };

  const kuraImageUrl = getImageUrl();

  // Get all column labels from API (including both participant and property columns)
  // Backend only returns visible columns in order
  const allColumns = kuraStartDetail?.getLotterySettingsList || [];

  // Table columns configuration from API
  // Map columns with their columnName to match with winner data
  const tableColumns = allColumns.map((setting, index) => ({
    key: `${setting.label}_${index}`, // Use label + index to ensure unique keys
    label: setting.label,
    columnName: setting.columnName, // Store columnName to match winner data
    index: index, // Store index for fallback
  }));
  console.log(tableColumns);
  // Calculate visible columns count for colspan
  const visibleColumnsCount = tableColumns.length;

  // Slider texts - fixed texts for the slider
  const sliderTexts = [
    "Hak sahibi sıra numaraları TOPLU KONUT İDARESİ BAŞKANLIĞI resmi internet sayfasında bulunan duyurular bölümündeki ilgili projenin bilgilendirme bölümünde yayınlanmıştır.",
    "Kura çekilişlerimiz K.V.K.K. gereği isim ve kimlik numaraları gizli tutularak gerçekleştirilmektedir.",
    "Kura Çekilişlerimiz Ankara 66. Noterliği denetiminde yapılmaktadır.",
    "Kura çekiliş sonuçları noter onayından sonra TOPLU KONUT İDARESİ BAŞKANLIĞI resmi internet sayfası www.toki.gov.tr adresinden ilan edilecektir.",
  ];

  // Initialize Embla Carousel with autoplay and disabled interactions
  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      watchDrag: false, // Disable mouse drag
      watchSlides: false, // Disable slide watching
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  // Show loading state while fetching data
  if (isDetailLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto p-8">
          <div className="space-y-6">
            <div className="container mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Image skeleton */}
              <div className="lg:col-span-8 xl:col-span-6 lg:order-2">
                <Skeleton className="w-full h-[400px] rounded-lg" />
              </div>
              {/* Stats skeleton */}
              <div className="lg:col-span-4 xl:col-span-3 lg:order-1 bg-card border rounded-lg shadow-sm">
                <div className="p-6 space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if data fetch failed
  if (detailError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-red-600">
            Veri yüklenirken hata oluştu
          </h2>
          <p className="text-muted-foreground">
            {detailError?.message || "Bilinmeyen hata"}
          </p>
          <Button onClick={() => window.location.reload()}>Tekrar Dene</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Fullscreen kura start page without header/sidebar */}
      <div className="mx-auto p-8 min-h-screen flex flex-col">
        <div className="space-y-6 mb-4">
          {/* First Row: 3 columns - Stats list in first column (smaller), second column larger */}
          <div className="container mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Second Column - Kura Image (shows first on mobile/tablet) */}
            <div className="lg:col-span-8 xl:col-span-6 lg:order-2 overflow-hidden max-h-[400px] h-full flex items-center justify-center">
              {isDetailLoading || !kuraImageUrl ? (
                <Skeleton className="w-full h-[400px] rounded-lg" />
              ) : (
                <img
                  src={kuraImageUrl}
                  alt="Kura Görseli"
                  className="w-full h-full object-contain max-h-[400px] rounded-lg"
                />
              )}
            </div>

            {/* First Column: Stats List (shows second on mobile/tablet) */}
            <div className="sticky top-4 lg:col-span-4 xl:col-span-3 lg:order-1 bg-card border rounded-lg shadow-sm flex flex-col">
              {/* Stats Section */}
              <div className="p-6 border-b">
                <div className="space-y-4">
                  {kuraStats.map((stat, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <span className="text-sm font-medium text-muted-foreground">
                        {stat.label}
                      </span>
                      <span className="text-xl font-bold">{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Slider Section */}
              <div className="p-6 flex-1 flex flex-col">
                {/* Embla Viewport - Non-interactive */}
                <div
                  className="overflow-hidden h-full pointer-events-none"
                  ref={emblaRef}
                >
                  {/* Embla Container */}
                  <div className="flex h-full">
                    {sliderTexts.map((text, index) => (
                      <div
                        key={index}
                        className="flex-[0_0_100%] min-w-0 flex items-center justify-center px-2"
                      >
                        <p className="text-sm text-muted-foreground text-center leading-relaxed">
                          {text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Third Column - Empty (4 cols) - Hidden on lg and below */}
            <div className="hidden xl:block xl:col-span-3 xl:order-3"></div>
          </div>

          {/* Second Row: Counters and Winners Table */}
          <div className="space-y-6">
            {/* Kura Name Row */}
            <div className="container mx-auto text-center py-4">
              <h2 className="text-2xl font-bold">{kuraName}</h2>
            </div>

            {/* Counters Row */}
            <div
              className={`container mx-auto grid gap-2 md:gap-8 ${isParticipantPropertyKind
                ? "grid-cols-1 md:grid-cols-2"
                : "grid-cols-1 max-w-2xl mx-auto"
                }`}
            >
              {/* Counter 1 - Participant Counter (show for lotteryKind 1 and 2) */}
              {(lotteryKind === 1 || isParticipantPropertyKind) && (
                <div className="p-4 md:p-6">
                  <div className="flex justify-center">
                    <SlotCounter
                      duration={0.1}
                      animateUnchanged={true}
                      value={counter1.toString().padStart(participantDigits, "0")}
                      charClassName="border-2 border-primary rounded-lg shadow-md"
                      containerClassName="flex space-x-2"
                      numberClassName="p-3 text-xl md:text-3xl font-bold font-mono"
                    />
                  </div>
                </div>
              )}

              {/* Counter 2 - Property Counter (show for lotteryKind 2 and 3) */}
              {(isParticipantPropertyKind || lotteryKind === 3) && (
                <div className="p-4 md:p-6">
                  <div className="flex justify-center">
                    <SlotCounter
                      duration={0.1}
                      animateUnchanged={true}
                      value={counter2.toString().padStart(propertyDigits, "0")}
                      charClassName="border-2 border-primary rounded-lg shadow-md"
                      containerClassName="flex space-x-2"
                      numberClassName="p-3 text-xl md:text-3xl font-bold font-mono"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Winners Table */}
            <div className="bg-card border rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      {tableColumns.map((column) => (
                        <TableHead
                          key={column.key}
                          className="whitespace-nowrap"
                        >
                          {column.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {winners.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={visibleColumnsCount}
                          className="p-0"
                        >
                          <div className="flex items-center justify-center py-12 px-6 md:p-12 sticky left-0 w-full max-w-[calc(100vw-4rem)]">
                            <div className="bg-card/95 backdrop-blur-sm border rounded-lg px-8 py-6 shadow-md max-w-full whitespace-normal">
                              <p className="text-lg font-medium text-center text-foreground">
                                Kazananlar henüz belirlenmedi
                              </p>
                              <p className="text-sm text-muted-foreground text-center mt-2">
                                Kura çekilişi sırasında kazananlar burada
                                görüntülenecektir.
                              </p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      winners.map((winner, winnerIndex) => (
                        <TableRow key={winnerIndex}>
                          {tableColumns.map((column) => {
                            // Match winner data using columnName from settings
                            const value = column.columnName ? winner[column.columnName] : null;

                            // Check if this is a "Durum" column for special styling
                            const isDurumColumn = column.label
                              .toLowerCase()
                              .includes("durum");
                            const primaryTitle = kuraStartDetail?.primaryTitle?.toString().toLowerCase();
                            const backupTitle = kuraStartDetail?.backupTitle?.toString().toLowerCase();
                            const currentValue = value?.toString().toLowerCase();
                            return (
                              <TableCell key={column.key}>
                                {isDurumColumn && value ? (
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium
          ${currentValue === primaryTitle
                                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                                        : currentValue === backupTitle
                                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                                          : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                                      }`}
                                  >
                                    {value}
                                  </span>
                                ) : (
                                  value ?? "-"
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer with Marquee and FAB */}
        <div className="sticky bottom-4 mt-auto z-50">
          <div className="flex items-center gap-4">
            {/* Marquee Section - flex-grow to take available space but with min-width 0 to allow shrinking */}
            <div className="flex-grow min-w-0 bg-gray-800 rounded-lg py-3">
              <Marquee speed={50} gradient={false}>
                <span className="text-lg font-semibold text-white mx-8">
                  {marqueeText}
                </span>
              </Marquee>
            </div>

            {/* Button Section - fixed width to prevent being pushed out */}
            <div className="flex-shrink-0 w-12">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    size="icon"
                    className="rounded-full w-12 h-12 shadow-lg"
                    aria-label="Menu"
                  >
                    <Menu className="h-7 w-7" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="end"
                  sideOffset={10}
                  className="w-auto p-2"
                >
                  <div className="flex flex-col gap-2">
                    {/* Homepage Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link to="/" className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        Ana Sayfa
                      </Link>
                    </Button>

                    {/* Kura List Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura"
                        search={{ search: "" }}
                        className="flex items-center gap-2"
                      >
                        <List className="h-4 w-4" />
                        Kura Listesi
                      </Link>
                    </Button>

                    {/* Kura Detail Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura/$kuraId/edit"
                        params={{ kuraId }}
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Kura Detay
                      </Link>
                    </Button>

                    {/* Winners List Button */}
                    <Button asChild variant="ghost" className="justify-start">
                      <Link
                        to="/kura/$kuraId/winners"
                        params={{ kuraId }}
                        className="flex items-center gap-2"
                      >
                        <Trophy className="h-4 w-4" />
                        Kazanan Listesi
                      </Link>
                    </Button>

                    {/* Start Kura Button */}
                    {!kuraStarted ? (
                      <Button
                        variant="ghost"
                        className="justify-start"
                        onClick={handleStartKura}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Kurayı Başlat
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        className="justify-start text-red-600 hover:text-red-700"
                        onClick={handleStopKura}
                      >
                        <Play className="h-4 w-4 mr-2 rotate-180" />
                        Kurayı Durdur
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
